import type { AxiosResponse } from 'axios';

export interface ApiEnvelope<T> {
  success?: boolean;
  code?: number;
  message?: string;
  data: T;
  timestamp?: string;
}

type EnvelopeLike<T> = {
  data: T;
  success?: boolean;
  code?: number;
  message?: string;
  timestamp?: string;
};

type UnwrappedEnvelope<T> = T extends EnvelopeLike<infer U> ? U : T;
const ENVELOPE_KEYS = new Set(['success', 'code', 'message', 'data', 'timestamp']);

function isEnvelope<T>(value: unknown): value is EnvelopeLike<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Object.keys(value).every((key) => ENVELOPE_KEYS.has(key))
  );
}

export function unwrapEnvelope<T>(payload: T): UnwrappedEnvelope<T> {
  if (!isEnvelope<UnwrappedEnvelope<T>>(payload)) {
    return payload as UnwrappedEnvelope<T>;
  }

  if (payload.success === false) {
    throw new Error(payload.message || `Request failed with code ${payload.code}`);
  }

  return payload.data as UnwrappedEnvelope<T>;
}

type CancelablePromiseLike<T> = PromiseLike<T> & {
  cancel?: () => void;
};

export async function unwrapServiceResponse<T>(
  promise: CancelablePromiseLike<T>,
  signal?: AbortSignal
): Promise<UnwrappedEnvelope<T>> {
  const cancel = () => promise.cancel?.();
  if (signal?.aborted) {
    cancel();
  } else {
    signal?.addEventListener('abort', cancel, { once: true });
  }
  try {
    return unwrapEnvelope(await promise);
  } finally {
    signal?.removeEventListener('abort', cancel);
  }
}

export async function unwrapAxiosResponse<T>(
  promise: PromiseLike<AxiosResponse<T>>,
): Promise<UnwrappedEnvelope<T>> {
  const response = await promise;
  return unwrapEnvelope(response.data);
}
